<template>
  <section id="paymentTransactionSelectList">
    <h3 class="offset-bottom-2">{{ $t('transactions.overview_headline') }}</h3>

    <transition name="fade" mode="out-in" appear>
      <VueGoodTable v-bind="table">
        <template #table-row-after="{ row, index }">
          <td>
            <VButton class="btn btn-default btn-small" label="transactions.details_button_label" @click.stop="selectRow(row, index)" />
          </td>
        </template>
        <template #table-column="{ column }">
          <VTableHeader :columns="table.columns" :column="column" locale-namespace="transactions" />
        </template>
        <div slot="emptystate" class="center-align text-disabled">
          {{ $t('common.errorNoData') }}
        </div>
      </VueGoodTable>
    </transition>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/Button';
import TableMixin from '@/mixins/table';
import TableUtilitiesMixin from '@/mixins/tableUtilities';
import { VueGoodTable } from 'vue-good-table';
import VTableHeader from '@/components/Table/Header';
import { PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS } from '@route-types';
import { formatDateTime } from '@/utils/date';
import selectListColumns from './selectListColumns';

export default {
  name: 'PaymentTransactionsList',

  components: {
    VueGoodTable,
    VButton,
    VTableHeader,
  },

  mixins: [TableMixin, TableUtilitiesMixin],

  props: {
    rows: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      transactionId: null,
      table: {
        columns: selectListColumns,
        rows: null,
        defaultSortBy: { field: 'transactionId', type: 'desc' },
      },
    };
  },

  computed: {
    ...mapGetters('system', ['hasShowCCTransactionView']),

    routeParamsTransactionId() {
      return this.$route.params.transactionId;
    },
  },

  watch: {
    rows: {
      handler: 'onRowsChanged',
      immediate: true,
    },
    routeParamsTransactionId: 'onTransactionIdChanged',
  },

  created() {
    this.prepareColumns();
  },

  methods: {
    onTransactionIdChanged(val) {
      if (!val) {
        this.transactionId = null;
        return;
      }

      const transactionId = parseInt(val, 10);
      const index = this.table.rows.findIndex(row => row.transactionId === transactionId);
      if (index < 0) {
        this.transactionId = null;
      } else {
        this.selectRow({ originalIndex: index }, index);
      }
    },

    onRowsChanged() {
      this.table.rows = this.translateRows(this.rows?.filter(row => !!row.transactionId));

      this.resetFilterDropDownListByField(this.table, 'partnerName');
      this.addFilterDropDownListByField(this.table, 'partnerName');

      if (this.table.columns.find(col => col.field === 'stateDescription')) {
        this.resetFilterDropDownListByField(this.table, 'stateDescription');
        this.addFilterDropDownListByField(this.table, 'stateDescription');
      }

      this.handleDefaultSearchFields(this.table);
      this.onTransactionIdChanged(this.routeParamsTransactionId);
    },

    translateRows(rows) {
      if (rows) {
        rows.map(row => {
          row.subscriptionChargeTypeId =
            row.subscriptionDataChargeType === 'RC_ONE_TIME'
              ? this.$t('transactions.subscriptionDataChargeType_RC_ONE_TIME')
              : this.$t(`transactions.subscriptionChargeTypeId_${row.subscriptionChargeTypeId}`);
          row.price = this.getPrice(row.retailChargeBooked, row.retailChargeAuthorized);
          row.transactionStart = formatDateTime(row.transactionStart);
          return row;
        });
      }
      return rows;
    },

    getPrice(retailChargeBooked, retailChargeAuthorized) {
      if (retailChargeBooked != null && retailChargeBooked.gross != null) {
        return this.formatCurrency(retailChargeBooked);
      } else if (retailChargeAuthorized != null && retailChargeAuthorized.gross != null) {
        return this.formatCurrency(retailChargeAuthorized);
      }
      return '';
    },

    formatCurrency(value) {
      // value with zero should be shown also
      if (value === null || value === undefined) {
        return ' ';
      }
      return new Intl.NumberFormat(this.$t('common.locateNumberFormat'), {
        style: 'currency',
        currency: value.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value.gross);
    },

    selectRow(row, index) {
      this.setAriaSelected(index);

      if (this.table.rows[row.originalIndex] !== undefined) {
        this.transactionId = this.table.rows[row.originalIndex].transactionId;
        this.$emit('select', this.table.rows[row.originalIndex]);
        const { transactionId, from, to, tab } = this.$route.params;
        if (parseInt(transactionId) === this.transactionId) return;
        this.$router
          .push({
            name: PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS,
            params: {
              from,
              to,
              transactionId: this.transactionId,
              tab,
            },
          })
          .catch(() => {});
      }
    },

    tableRowStyle(row) {
      if (this.transactionId !== null && row.transactionId === this.transactionId) {
        return 'brand';
      }
      return '';
    },

    prepareColumns() {
      this.table.columns = selectListColumns.filter(col => {
        if (this.hasShowCCTransactionView) {
          return !col.customerOnly;
        }
        return !col.agentOnly;
      });
    },
  },
};
</script>
